import React from 'react';
import { Col, Row, Radio, Checkbox } from 'antd';
import styled from 'styled-components';
import { RadioChangeEvent } from 'antd/lib';
import { FormikContextType } from 'formik';
import moment from 'moment';

import { useIntl } from 'common/config/i18n';
import InputSelect from 'components/organisms/select';
import FormError from 'components/molecules/form-error';
import { ConflictType, SendCommunicationFirstStepForm, Typology } from 'types';
import DatePickerForMomentDates from 'components/organisms/datepicker';
import Input from 'components/organisms/input';
import { getSelectPurchaseDateTimeList } from 'common/utils/send-communication';

export type Props = {
  formik: FormikContextType<SendCommunicationFirstStepForm>;
  typologies: Typology[];
  conflictTypes: ConflictType[];
  onTouch: () => void;
  checkOthers: boolean;
  setCheckOthers: (e: boolean) => void;
  isOnlyConflictedTickets: boolean;
  setIsOnlyConflictedTickets: (e: boolean) => void;
  isExcludeConflictedTickets: boolean;
  setIsExcludeConflictedTickets: (e: boolean) => void;
  isVisible: boolean;
};
type OthersCheckboxProps = {
  checkOthers: boolean;
  handleCheckOthers: (e: RadioChangeEvent) => void;
};
type TypologiesTargetProps = {
  typologies: Typology[];
  selectedTypologiesIds: number[];
  onSelectTypologies: (value: number[]) => void;
};
type ConflictsTargetProps = {
  formik: FormikContextType<SendCommunicationFirstStepForm>;
  conflictTypes: ConflictType[];
  isOnlyConflictedTickets: boolean;
  handleOnlyConflictedTickets: (e: RadioChangeEvent) => void;
  isExcludeConflictedTickets: boolean;
  handleExcludeConflictedTickets: (e: RadioChangeEvent) => void;
  selectedConflictTypeId: number | undefined;
  onSelectConflictType: (value: number) => void;
};
type PurchaseTimeTargetProps = {
  formik: FormikContextType<SendCommunicationFirstStepForm>;
  selectedPurchaseComparison: SendCommunicationFirstStepForm['selectedPurchaseComparison'];
  onSelectPurchase: (id: SendCommunicationFirstStepForm['selectedPurchaseComparison']) => void;
  selectedPurchaseDate: string | undefined;
  onSelectPurchaseDate: (date: string) => void;
  selectedPurchaseTime: string | undefined;
  onSelectPurchaseTime: (time: string) => void;
};

export const Container = styled.div<{ isVisible: boolean }>`
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  ${({ isVisible }) => !isVisible && 'height: 0'};
  overflow: hidden;
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-20px')});
  transition: opacity 0.8s, transform 0.5s;
`;

const StyledText = styled.div<{ isTitle?: boolean }>`
  ${(props) => props?.isTitle && `font-size: ${props.theme.font.size.h3}`};
  font-weight: bold;
  margin-right: 10px;
`;

export const OthersCheckbox = ({ checkOthers, handleCheckOthers }: OthersCheckboxProps) => {
  const { t } = useIntl();

  return (
    <Row style={{ marginBottom: 20 }}>
      <Col style={{ marginRight: '0.5rem' }}>
        <StyledText isTitle>{t('sendCommunication', 'otherSelection', 'headText')}</StyledText>
      </Col>
      <Radio.Group value={checkOthers} onChange={handleCheckOthers} name="radiogroup">
        <Row align="middle" style={{ marginLeft: 25 }}>
          <Radio value={false}>{t('form', 'NO')}</Radio>
          <Radio value>{t('form', 'YES')}</Radio>
        </Row>
      </Radio.Group>
    </Row>
  );
};

export const TypologiesTarget = ({ typologies, selectedTypologiesIds, onSelectTypologies }: TypologiesTargetProps) => {
  const { t } = useIntl();

  const typologiesData = [
    {
      id: 'typologies',
      list:
        typologies?.map((typology, index) => ({
          id: typology.id || `${typology.name}-${index}}`,
          name: typology.name,
        })) || [],
    },
  ];

  return (
    <Row align="middle" style={{ marginBottom: '1rem' }}>
      <Col span={3} style={{ marginRight: '0.5rem' }}>
        <StyledText>{t('sendCommunication', 'otherSelection', 'typologyText')}</StyledText>
      </Col>
      <Col>
        <InputSelect
          mode="multiple"
          onChange={(ids) => {
            onSelectTypologies(ids as number[]);
          }}
          value={selectedTypologiesIds as number[]}
          data={typologiesData}
          style={{ width: 350 }}
        />
      </Col>
    </Row>
  );
};

export const ConflictsTarget = ({
  formik,
  conflictTypes,
  isOnlyConflictedTickets,
  handleOnlyConflictedTickets,
  isExcludeConflictedTickets,
  handleExcludeConflictedTickets,
  selectedConflictTypeId,
  onSelectConflictType,
}: ConflictsTargetProps) => {
  const { t } = useIntl();

  const conflictTypesData = [
    {
      id: 'conflictTypesData',
      list: conflictTypes?.filter((conflictTypes) => conflictTypes.isEnabled),
    },
  ];

  return (
    <Col style={{ marginBottom: '1rem' }}>
      <Row align="middle">
        <Col span={3} style={{ marginRight: '0.5rem' }}>
          <StyledText>{t('sendCommunication', 'otherSelection', 'conflictedTickets', 'label')}</StyledText>
        </Col>
        <Col>
          <Row align="middle">
            <Checkbox
              name="onlyConflictedTickets"
              checked={isOnlyConflictedTickets}
              onChange={handleOnlyConflictedTickets}
              disabled={isExcludeConflictedTickets}
            >
              <StyledText>{t('sendCommunication', 'otherSelection', 'conflictedTickets', 'onlyLabel')}</StyledText>
            </Checkbox>
            <Col>
              <Row style={{ position: 'absolute', top: '35px', left: '10px' }}>
                <FormError formik={formik} fieldName="selectedConflictTypeId" />
              </Row>
              <InputSelect
                onChange={(id) => {
                  onSelectConflictType(id as number);
                }}
                data={conflictTypesData}
                value={selectedConflictTypeId}
                disabled={!isOnlyConflictedTickets}
                style={{ width: 360, marginLeft: 10 }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={3} style={{ marginRight: '0.5rem' }}></Col>
        <Col>
          <Checkbox
            name="excludeConflictedTickets"
            checked={isExcludeConflictedTickets}
            onChange={handleExcludeConflictedTickets}
            disabled={isOnlyConflictedTickets}
          >
            <StyledText>{t('sendCommunication', 'otherSelection', 'conflictedTickets', 'excludeLabel')}</StyledText>
          </Checkbox>
        </Col>
      </Row>
    </Col>
  );
};

export const PurchaseTimeTarget = ({
  formik,
  selectedPurchaseComparison,
  onSelectPurchase,
  selectedPurchaseDate,
  onSelectPurchaseDate,
  selectedPurchaseTime,
  onSelectPurchaseTime,
}: PurchaseTimeTargetProps) => {
  const { t } = useIntl();

  const purchaseTimeData = [
    {
      id: 'purchaseTimeData',
      list: getSelectPurchaseDateTimeList(t),
    },
  ];

  const hasPurchaseErrors =
    (formik.touched.purchaseReferenceDate && formik.errors.purchaseReferenceDate === 'INCOMPLETE_ERROR') ||
    (formik.touched.purchaseReferenceTime && formik.errors.purchaseReferenceTime === 'INCOMPLETE_ERROR');

  return (
    <>
      <Row align="middle">
        <Col span={3} style={{ marginRight: '0.5rem' }}>
          <StyledText>{t('sendCommunication', 'otherSelection', 'selectPurchase', 'label')}</StyledText>
        </Col>
        <Col>
          <Row>
            <InputSelect
              onChange={(id) => {
                onSelectPurchase(id as SendCommunicationFirstStepForm['selectedPurchaseComparison']);
              }}
              data={purchaseTimeData}
              style={{ width: 200 }}
              value={selectedPurchaseComparison}
            />
            <Row align="middle" style={{ marginLeft: 20 }}>
              <StyledText>{t('sendCommunication', 'otherSelection', 'selectPurchase', 'date')}</StyledText>
              <DatePickerForMomentDates
                allowClear={false}
                value={selectedPurchaseDate ? moment(selectedPurchaseDate) : null}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  onSelectPurchaseDate(date ? moment(date).format('YYYY-MM-DD') : '');
                }}
                disabled={!selectedPurchaseComparison}
              />
            </Row>
            <Row align="middle" style={{ marginLeft: 20 }}>
              <StyledText>{t('sendCommunication', 'otherSelection', 'selectPurchase', 'hour')}</StyledText>
              <Input
                type="time"
                height="auto"
                value={selectedPurchaseTime}
                onChange={(e) => {
                  onSelectPurchaseTime(e.target.value);
                }}
                disabled={!selectedPurchaseComparison}
              />
            </Row>
          </Row>
          {hasPurchaseErrors && (
            <FormError formik={formik} message={t('sendCommunication', 'otherSelection', 'selectPurchase', 'error')} />
          )}
        </Col>
      </Row>
    </>
  );
};
